import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { TutorialPrompt } from './TutorialPrompt';
import { trackEvent } from '../../utils/analytics_utils';
import { useGlobalTutorial } from './GlobalTutorialContext';
import { Box } from '@mui/material';
import { useTutorialProgress, useUpdateTutorialProgressMutation } from '@features/user';

interface TutorialContextType {
  isActive: boolean;
  currentStep: number;
  totalSteps: number;
  startTutorial: () => void;
  skipTutorial: () => void;
  endTutorial: () => void;
  nextStep: () => void;
  previousStep: () => void;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

interface TutorialProviderProps {
  children: React.ReactNode;
  tutorialKey: string;
  tutorialVersion?: number;
  totalSteps: number;
  tutorialDisabled?: boolean;
  onTutorialStart?: () => void;
  onTutorialEnd?: () => void;
  autoStart?: boolean;

  // Legacy props (backwards compatible)
  promptTitle?: string;
  promptDescription?: string;

  // New customization props for more flexibility
  customHeader?: string; // Overrides the dialog header text
  customDescription?: string; // Overrides the dialog description text
  hideSubtext?: boolean; // Hides the "This should take less than 1 minute!" text
  customAcceptText?: string; // Custom text for the accept button (default: "Show Tutorial")
  customDeclineText?: string; // Custom text for the decline button (default: "Skip")
  customContent?: React.ReactNode; // Completely override the dialog content
}

export const TutorialProvider: React.FC<TutorialProviderProps> = ({
  children,
  tutorialKey,
  tutorialVersion = 1,
  totalSteps,
  tutorialDisabled = false,
  onTutorialStart,
  onTutorialEnd,
  autoStart = false,
  promptTitle,
  promptDescription,
  customHeader,
  customDescription,
  hideSubtext,
  customAcceptText,
  customDeclineText,
  customContent,
}) => {
  const [isActive, setIsActive] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [maxStepReached, setMaxStepReached] = useState(0);
  const { registerTutorial, unregisterTutorial } = useGlobalTutorial();
  const tutorialProgressData = useTutorialProgress();
  const [updateTutorialProgress] = useUpdateTutorialProgressMutation();

  const hasAccepted = useRef(false);

  useEffect(() => {
    if (tutorialDisabled) return;
    if (tutorialProgressData === undefined) return;
    if (hasAccepted.current) return;

    // FOR DEVELOPMENT: Set this to true to force tutorials to show regardless of localStorage
    const forceShowTutorial = false;

    const tutorialProgress = tutorialProgressData?.[tutorialKey];

    const noProgressCheck = !tutorialProgress;

    const statusCheck =
      tutorialProgress?.status !== 'completed' && tutorialProgress?.status !== 'dismissed';

    const versionCheck = tutorialProgress?.version && tutorialProgress.version < tutorialVersion;

    const shouldShowTutorial = noProgressCheck || statusCheck || versionCheck;

    // Only show tutorial if it hasn't been seen before
    if (shouldShowTutorial || forceShowTutorial) {
      if (autoStart) {
        setTimeout(() => {
          handleAcceptTutorial();
        }, 1000);
      } else {
        setShowPrompt(true);
      }
    }
  }, [tutorialKey, tutorialDisabled, autoStart, tutorialProgressData]);

  const handleAcceptTutorial = () => {
    hasAccepted.current = true;
    setShowPrompt(false);
    setStartTime(new Date());
    if (onTutorialStart) {
      onTutorialStart();
    }
    setTimeout(() => {
      setIsActive(true);
    }, 100);
    updateTutorialProgress({
      tutorial_id: tutorialKey,
      updated_fields: {
        status: 'in_progress',
        version: tutorialVersion,
        current_step: currentStep,
      },
    });
  };

  const handleDeclineTutorial = () => {
    setShowPrompt(false);
    trackEvent({
      event: 'tutorial_declined',
      tutorial_id: tutorialKey,
    });
    updateTutorialProgress({
      tutorial_id: tutorialKey,
      updated_fields: {
        status: 'dismissed',
        version: tutorialVersion,
      },
    });
    setIsActive(false);
    setCurrentStep(0);
    setStartTime(null);
    setMaxStepReached(0);
  };

  const startTutorial = useCallback(() => {
    setIsActive(true);
    setCurrentStep(0);
    setStartTime(new Date());
    updateTutorialProgress({
      tutorial_id: tutorialKey,
      updated_fields: {
        status: 'in_progress',
        version: tutorialVersion,
        current_step: 0,
      },
    });
  }, [tutorialKey, tutorialVersion]);

  // Register this tutorial with the global context
  useEffect(() => {
    if (!tutorialDisabled) {
      registerTutorial({
        key: tutorialKey,
        startTutorial,
      });
    }

    return () => {
      unregisterTutorial(tutorialKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorialKey, startTutorial, tutorialDisabled]);

  const endTutorial = useCallback(() => {
    if (startTime) {
      const duration = new Date().getTime() - startTime.getTime();
      trackEvent({
        event: 'tutorial_completed',
        tutorial_id: tutorialKey,
        duration_ms: duration,
        max_step_reached: maxStepReached + 1,
        total_steps: totalSteps,
        completed_percentage: Math.round(((maxStepReached + 1) / totalSteps) * 100),
      });
    }
    updateTutorialProgress({
      tutorial_id: tutorialKey,
      updated_fields: {
        status: 'completed',
        version: tutorialVersion,
        current_step: currentStep,
      },
    });
    if (onTutorialEnd) {
      onTutorialEnd();
    }
    setIsActive(false);
    setCurrentStep(0);
    setStartTime(null);
    setMaxStepReached(0);
  }, [startTime, maxStepReached, totalSteps, tutorialKey]);

  const nextStep = useCallback(() => {
    setCurrentStep(prev => {
      const nextStep = prev < totalSteps - 1 ? prev + 1 : prev;
      setMaxStepReached(current => Math.max(current, nextStep));
      updateTutorialProgress({
        tutorial_id: tutorialKey,
        updated_fields: {
          current_step: nextStep,
        },
      });
      return nextStep;
    });
  }, [totalSteps]);

  const previousStep = useCallback(() => {
    setCurrentStep(prev => {
      const prevStep = prev > 0 ? prev - 1 : prev;
      updateTutorialProgress({
        tutorial_id: tutorialKey,
        updated_fields: {
          current_step: prevStep,
        },
      });
      return prevStep;
    });
  }, [tutorialKey]);

  return (
    <TutorialContext.Provider
      value={{
        isActive,
        currentStep,
        totalSteps,
        startTutorial,
        skipTutorial: handleDeclineTutorial,
        endTutorial,
        nextStep,
        previousStep,
      }}
    >
      {/* Global overlay that appears when tutorial is active */}
      <Box
        id="global-tutorial-overlay"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1400,
          display: isActive ? 'block' : 'none',
          pointerEvents: 'all',
        }}
      />
      <TutorialPrompt
        open={showPrompt}
        onAccept={handleAcceptTutorial}
        onDecline={handleDeclineTutorial}
        tutorialKey={tutorialKey}
        title={promptTitle}
        description={promptDescription}
        customHeader={customHeader}
        customDescription={customDescription}
        hideSubtext={hideSubtext}
        customAcceptText={customAcceptText}
        customDeclineText={customDeclineText}
        customContent={customContent}
      />
      {children}
    </TutorialContext.Provider>
  );
};
